import React, { useEffect, useState } from "react";
import { api, formatCHF, currentMonthString, formatDatum } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { PageContainer, PageHeader, MonthPicker } from "../components/PageHeader";
import { Plus, Pencil, Trash2, Lightbulb, Upload, Sparkles } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  datum: new Date().toISOString().slice(0, 10),
  tournummer: "",
  fahrer_id: "",
  fahrzeug_id: "",
  umsatz_kunde: 0,
  fahrerlohn: 0,
  bemerkung: "",
  status: "offen",
};

function suggestLohn(umsatz) {
  const u = Number(umsatz) || 0;
  if (u >= 150) return 100;
  if (u >= 110) return 90;
  return null;
}

export default function Quickmail() {
  const [monat, setMonat] = useState(currentMonthString());
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const planFileRef = React.useRef();
  const [planUploading, setPlanUploading] = useState(false);
  const [planResult, setPlanResult] = useState(null);
  const [planResultOpen, setPlanResultOpen] = useState(false);
  const aufwandFileRef = React.useRef();
  const [aufwandUploading, setAufwandUploading] = useState(false);

  const load = async () => {
    const [r, e, v] = await Promise.all([
      api.get("/qm-tours", { params: { monat } }),
      api.get("/employees"),
      api.get("/vehicles"),
    ]);
    setList(r.data);
    setEmployees(e.data);
    setVehicles(v.data);
  };
  useEffect(() => {
    load();
  }, [monat]);

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";
  const vehName = (id) => vehicles.find((v) => v.id === id)?.kennzeichen || "—";

  const uploadPlan = async (file) => {
    setPlanUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("monat", monat);
    try {
      const r = await api.post("/qm-tours/upload-plan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPlanResult(r.data);
      setPlanResultOpen(true);
      toast.success(`${r.data.created} QM-Touren erstellt aus Planung (${r.data.rows_parsed} Zeilen erkannt)`);
      load();
    } catch (e) {
      toast.error("Plan-Upload fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setPlanUploading(false);
      if (planFileRef.current) planFileRef.current.value = "";
    }
  };

  const uploadAufwand = async (file) => {
    setAufwandUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("monat", monat);
    try {
      const r = await api.post("/qm-tours/upload-aufwand", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Aufwand verarbeitet: ${r.data.updated} aktualisiert · ${r.data.created} neu erstellt · ${r.data.skipped} übersprungen`);
      load();
    } catch (e) {
      toast.error("Aufwand-Upload fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setAufwandUploading(false);
      if (aufwandFileRef.current) aufwandFileRef.current.value = "";
    }
  };

  const handleUmsatzChange = (val) => {
    const s = suggestLohn(val);
    setForm((f) => ({ ...f, umsatz_kunde: val, fahrerlohn: s != null && (!editing || f.fahrerlohn === 0) ? s : f.fahrerlohn }));
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        umsatz_kunde: Number(form.umsatz_kunde) || 0,
        fahrerlohn: Number(form.fahrerlohn) || 0,
        fahrzeug_id: form.fahrzeug_id || null,
      };
      if (editing) await api.put(`/qm-tours/${editing.id}`, payload);
      else await api.post("/qm-tours", payload);
      toast.success("QM-Tour gespeichert");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Fehler");
    }
  };

  const suggestion = suggestLohn(form.umsatz_kunde);

  return (
    <PageContainer>
      <PageHeader title="Quickmail" subtitle="QM-Touren (i.d.R. mittwochs)" testId="qm-page">
        <MonthPicker value={monat} onChange={setMonat} testId="qm-month-picker" />
        <input
          type="file"
          ref={planFileRef}
          className="hidden"
          accept="image/*,application/pdf"
          onChange={(e) => e.target.files?.[0] && uploadPlan(e.target.files[0])}
          data-testid="qm-plan-input"
        />
        <Button
          variant="outline"
          onClick={() => planFileRef.current?.click()}
          disabled={planUploading}
          data-testid="qm-plan-upload-button"
          className="border-neutral-300"
        >
          {planUploading ? <Sparkles className="h-4 w-4 mr-2 animate-pulse" /> : <Upload className="h-4 w-4 mr-2" />}
          {planUploading ? "KI parst…" : "QM-Plan hochladen"}
        </Button>
        <input
          type="file"
          ref={aufwandFileRef}
          className="hidden"
          accept="image/*,application/pdf"
          onChange={(e) => e.target.files?.[0] && uploadAufwand(e.target.files[0])}
          data-testid="qm-aufwand-input"
        />
        <Button
          variant="outline"
          onClick={() => aufwandFileRef.current?.click()}
          disabled={aufwandUploading}
          data-testid="qm-aufwand-upload-button"
          className="border-neutral-300"
        >
          {aufwandUploading ? <Sparkles className="h-4 w-4 mr-2 animate-pulse" /> : <Upload className="h-4 w-4 mr-2" />}
          {aufwandUploading ? "KI parst…" : "QM-Aufwand (Umsatz)"}
        </Button>
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="add-qm-button">
          <Plus className="h-4 w-4 mr-2" /> Neue QM-Tour
        </Button>
      </PageHeader>

      <Card className="border-neutral-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead><TableHead>Tour</TableHead><TableHead>Fahrer</TableHead>
                <TableHead>Fahrzeug</TableHead><TableHead className="text-right">Umsatz</TableHead>
                <TableHead className="text-right">Fahrerlohn</TableHead><TableHead className="text-right">Gewinn</TableHead>
                <TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.length === 0 && (<TableRow><TableCell colSpan={9} className="text-center text-neutral-500 py-8">Keine QM-Touren.</TableCell></TableRow>)}
              {list.map((q) => (
                <TableRow key={q.id}>
                  <TableCell className="font-mono">{formatDatum(q.datum)}</TableCell>
                  <TableCell className="font-medium">{q.tournummer}</TableCell>
                  <TableCell>{empName(q.fahrer_id)}</TableCell>
                  <TableCell className="font-mono">{vehName(q.fahrzeug_id)}</TableCell>
                  <TableCell className="nivara-num">{formatCHF(q.umsatz_kunde)}</TableCell>
                  <TableCell className="nivara-num">{formatCHF(q.fahrerlohn)}</TableCell>
                  <TableCell className="nivara-num text-[#008A00] font-medium">{formatCHF((q.umsatz_kunde||0)-(q.fahrerlohn||0))}</TableCell>
                  <TableCell><Badge className={q.status === "abgerechnet" ? "bg-[#008A00] text-white" : q.status === "geprüft" ? "bg-[#0055FF] text-white" : "bg-[#FFB800] text-neutral-900"}>{q.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(q); setForm({ ...EMPTY, ...q, fahrzeug_id: q.fahrzeug_id || "" }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={async () => { if(window.confirm("Löschen?")){ await api.delete(`/qm-tours/${q.id}`); load();}}}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="qm-dialog">
          <DialogHeader><DialogTitle>{editing ? "QM-Tour bearbeiten" : "Neue QM-Tour"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Datum</Label><Input type="date" value={form.datum} onChange={(e) => setForm({...form, datum: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Tournummer</Label><Input value={form.tournummer} onChange={(e) => setForm({...form, tournummer: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2">
              <Label>Fahrer</Label>
              <Select value={form.fahrer_id} onValueChange={(v) => setForm({...form, fahrer_id: v})}>
                <SelectTrigger><SelectValue placeholder="Fahrer wählen…" /></SelectTrigger>
                <SelectContent>{employees.map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fahrzeug</Label>
              <Select value={form.fahrzeug_id || "_none"} onValueChange={(v) => setForm({...form, fahrzeug_id: v === "_none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">— Kein Fahrzeug —</SelectItem>{vehicles.map((v) => (<SelectItem key={v.id} value={v.id}>{v.kennzeichen}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Umsatz Kunde (CHF)</Label>
              <Input type="number" step="0.01" value={form.umsatz_kunde} onChange={(e) => handleUmsatzChange(e.target.value)} className="font-mono" data-testid="qm-umsatz-input" />
              {suggestion != null && (
                <div className="text-xs text-[#0055FF] flex items-center gap-1.5"><Lightbulb className="h-3.5 w-3.5" /> Vorgeschlagener Fahrerlohn: <span className="font-mono font-semibold">CHF {suggestion.toFixed(2)}</span> (überschreibbar)</div>
              )}
              {form.umsatz_kunde && suggestion == null && (
                <div className="text-xs text-amber-700 flex items-center gap-1.5"><Lightbulb className="h-3.5 w-3.5" /> Umsatz &lt; CHF 110.00 — keine automatische Berechnung, manuelle Eingabe erforderlich.</div>
              )}
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Fahrerlohn (CHF) — manuell editierbar</Label>
              <Input type="number" step="0.01" value={form.fahrerlohn} onChange={(e) => setForm({...form, fahrerlohn: e.target.value})} className="font-mono" data-testid="qm-fahrerlohn-input" />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["offen","geprüft","abgerechnet"].map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2"><Label>Bemerkung</Label><Input value={form.bemerkung || ""} onChange={(e) => setForm({...form, bemerkung: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="qm-save">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={planResultOpen} onOpenChange={setPlanResultOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="qm-plan-result-dialog">
          <DialogHeader>
            <DialogTitle>QM-Plan importiert</DialogTitle>
          </DialogHeader>
          {planResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <Card className="p-3 border-neutral-200"><div className="text-xs text-neutral-500 uppercase tracking-wider">Erkannt</div><div className="text-xl font-semibold">{planResult.rows_parsed}</div></Card>
                <Card className="p-3 border-neutral-200"><div className="text-xs text-neutral-500 uppercase tracking-wider">Erstellt</div><div className="text-xl font-semibold text-[#008A00]">{planResult.created}</div></Card>
                <Card className="p-3 border-neutral-200"><div className="text-xs text-neutral-500 uppercase tracking-wider">Übersprungen</div><div className="text-xl font-semibold text-neutral-500">{planResult.skipped}</div></Card>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Erstellte QM-Touren</div>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {(planResult.items || []).map((it) => (
                    <div key={it.id} className="text-xs flex items-center gap-2 py-1 border-b border-neutral-100 last:border-0">
                      <span className="font-mono w-20">{formatDatum(it.datum)}</span>
                      <span className="font-mono font-semibold w-12">{it.tournummer}</span>
                      <span className="flex-1">
                        {it.fahrer_match ? (
                          <span className="text-[#008A00]">✓ {it.fahrer_match.name}</span>
                        ) : (
                          <span className="text-amber-700">⚠ {it.ocr_fahrer || "kein Fahrer"}</span>
                        )}
                      </span>
                      <span className="text-neutral-600 text-right">
                        {it.fahrzeug_match ? `🚐 ${it.fahrzeug_match.kennzeichen}` : (it.ocr_fahrzeug ? `⚠ ${it.ocr_fahrzeug}` : "—")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              {planResult.warnings && planResult.warnings.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-amber-700 mb-2">Hinweise</div>
                  <ul className="text-xs space-y-1 list-disc pl-5">
                    {planResult.warnings.slice(0, 10).map((w, i) => (<li key={i}>{w.msg} ({formatDatum(w.datum)} · {w.tournummer})</li>))}
                  </ul>
                </div>
              )}
              <div className="text-xs text-neutral-500 pt-2 border-t border-neutral-100">
                Umsatz und Fahrerlohn sind initial 0.00 — der KI-Auto-Abgleich im Monatsabschluss füllt diese aus der 7Days-Rechnung.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setPlanResultOpen(false)} className="bg-[#0055FF] hover:bg-[#0040CC]">Verstanden</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
